import { useNavigate } from 'react-router';
import { CampaignListItem } from 'src/api/campaigns/types';
import { useGetCampaignsQuery } from 'src/api/campaigns/hooks';
import { DataTable } from 'src/components/DataTable';
import { ActionMenu } from 'src/components/DataTable/ActionMenu';
import { Td } from 'src/components/DataTable/Td';
import { links } from 'src/router/routes';
import { useCallback, useMemo } from 'react';
import { useGlobalModals } from 'src/contexts/global-modals';

const columns = [
  {
    key: 'id',
    label: 'ID',
  },
  {
    key: 'name',
    label: 'Name',
  },
  {
    key: 'date_from',
    label: 'Start Date',
  },
  {
    key: 'date_to',
    label: 'End Date',
  },
  {
    key: 'updated_at',
    label: 'Last updated at',
  },
];

export const CampaignsDataTable = () => {
  const { data: campaigns, isLoading } = useGetCampaignsQuery();
  const { openDeleteCampaignModal } = useGlobalModals();
  const navigate = useNavigate();

  const handleRowClick = useCallback(
    (campaignId: number) => {
      navigate(`${links.campaign}/${campaignId}`);
    },
    [navigate]
  );

  const getContent = useCallback(
    (campaign: CampaignListItem) => {
      return (
        <>
          <Td>{campaign.id}</Td>
          <Td>{campaign.name}</Td>
          <Td>{new Date(campaign.date_from).toLocaleDateString()}</Td>
          <Td>{new Date(campaign.date_to).toLocaleDateString()}</Td>
          <Td fullWidth>
            <span>{new Date(campaign.updated_at).toLocaleDateString()}</span>
            <ActionMenu
              onView={() => navigate(`${links.campaign}/${campaign.id}`)}
              onEdit={() => navigate(`${links.campaign}/${campaign.id}/edit`)}
              onRemove={() => openDeleteCampaignModal(campaign.id)}
            />
          </Td>
        </>
      );
    },
    [navigate]
  );

  const getRows = useCallback((campaign: CampaignListItem) => {
    return {
      id: String(campaign.id),
      name: campaign.name,
      date_from: campaign.date_from,
      date_to: campaign.date_to,
      updated_at: campaign.updated_at,
    };
  }, []);

  const data = useMemo(() => {
    return (
      campaigns?.map((campaign) => ({
        content: getContent(campaign),
        onRowClick: () => handleRowClick(campaign.id),
        rows: getRows(campaign),
      })) || []
    );
  }, [campaigns, getContent, getRows, handleRowClick]);

  return <DataTable columns={columns} data={data} isLoading={isLoading} />;
};
